import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CompanyForm from "~/forms/CompanyForm";
import { Badge, Box, Grid, Icon, InputCheckbox, InputText, Label, Picture, Text } from "~/components-shared";
import api from "~/lib/api";
import { DataUsageTypeEnum } from "~/types/enums";
import styled, { useTheme } from "styled-components";
import InputSelectControl from "~/components/InputSelectControl";
import lists from "~/lib/lists";
import InputToggleControl from "~/components/InputToggleControl";
import InputTextControl from "~/components/InputTextControl";
import InputTextAreaControl from "~/components/InputTextAreaControl";
import { helpers } from "~/lib/helpers";
import InputTextArea from "~/components-shared/InputTextArea";
const StyledGridCell = styled(Grid.Cell)`
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const Authorities = styled.div`
  flex-grow: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 400px;
`;
const ManageCompanyForm = _ref => {
  let {
    company,
    control,
    watch,
    setValue,
    setError
  } = _ref;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [authorities, setAuthorities] = useState([]);
  let tempAuthorities = authorities;
  const integrationTypes = company?.integrations || [];
  const [search, setSearch] = useState("");
  useEffect(() => {
    api.fetchManageAuthorities().then(response => {
      setAuthorities(response);
    });
  }, []);
  if (search) {
    tempAuthorities = authorities.filter(authority => authority.includes(search));
  }
  const groupedAuthorities = tempAuthorities ? tempAuthorities.reduce((groups, authority) => {
    const prevGroups = groups;
    const [mainPart] = authority.split(".");
    const [levels] = mainPart.split(":");
    const [primary] = levels.split("/");
    if (!prevGroups[primary]) {
      prevGroups[primary] = [];
    }
    groups[primary].push(authority);
    return prevGroups;
  }, {}) : {};
  const handleChange = authority => {
    const selected = watch("authorities");
    if (selected.includes(authority)) {
      setValue("authorities", selected.filter(item => item !== authority));
    } else {
      setValue("authorities", [...selected, authority]);
    }
  };
  const handleChangeGroup = key => {
    const selected = watch("authorities");
    const filtered = selected.filter(item => !item.startsWith(key));
    const selectedAll = watch("authorities").sort().join().includes(groupedAuthorities[key].join());
    if (selectedAll) {
      setValue("authorities", filtered);
    } else {
      setValue("authorities", [...filtered, ...authorities.filter(item => item.startsWith(key))]);
    }
  };
  return /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    equal: true,
    fullHeight: true
  }, /*#__PURE__*/React.createElement(StyledGridCell, {
    s: {
      pl: 1,
      pr: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 3
    }
  }, "Basic Settings"), /*#__PURE__*/React.createElement(CompanyForm, {
    control: control,
    watch: watch,
    setValue: setValue,
    setError: setError,
    company: company
  }), /*#__PURE__*/React.createElement(Label, {
    s: {
      mt: 3,
      mb: 2
    },
    required: true
  }, t("MemberForm.authorities")), /*#__PURE__*/React.createElement(InputText, {
    value: search,
    placeholder: t("ManageCompanyForm.searchAuthorities"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: "textSecondary",
      src: "search",
      s: {
        mr: 2
      }
    }),
    onChange: value => setSearch(value),
    s: {
      mb: 3
    },
    clearButton: true
  }), /*#__PURE__*/React.createElement(Authorities, null, Object.keys(groupedAuthorities).map(key => /*#__PURE__*/React.createElement(React.Fragment, {
    key: key
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: watch("authorities").sort().join().includes(groupedAuthorities[key].join()),
    onChange: () => handleChangeGroup(key),
    label: /*#__PURE__*/React.createElement(Text, {
      element: "h3"
    }, key)
  }), groupedAuthorities[key].map(authority => /*#__PURE__*/React.createElement(InputCheckbox, {
    key: authority,
    value: watch("authorities").includes(authority),
    onChange: () => handleChange(authority),
    label: authority,
    color: helpers.sensitiveAuthorities.includes(authority) ? "danger" : "primary",
    s: {
      ml: 5
    }
  })))))), /*#__PURE__*/React.createElement(StyledGridCell, {
    s: {
      pl: 1,
      pr: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 3
    }
  }, "Internal Settings"), /*#__PURE__*/React.createElement(InputTextControl, {
    name: "weight",
    label: t("ManageCompanyForm.weight"),
    s: {
      mb: 5
    },
    control: control,
    type: "number",
    required: true
  }), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "settings.isPaidPlan",
    label: t("ManageCompanyForm.isPaidPlan"),
    control: control,
    display: "block",
    s: {
      mb: 3
    }
  }), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "settings.sendSlackStatusReport",
    label: t("ManageCompanyForm.sendSlackStatusReport"),
    control: control,
    display: "block",
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextAreaControl, {
    name: "context",
    label: t("ManageCompanyForm.context"),
    s: {
      mb: 5
    },
    control: control
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 3
    }
  }, "Security"), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "settings.ssoAuthentication",
    label: t("ManageCompanyForm.ssoAuthentication"),
    control: control
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mt: 10,
      mb: 3
    }
  }, "Integrations"), /*#__PURE__*/React.createElement(InputSelectControl, {
    s: {
      mb: 5
    },
    label: t("ManageCompanyForm.recommendedIntegrations"),
    name: "settings.recommendedIntegrations",
    options: lists.integrations.types.names,
    control: control,
    placeholder: "Select Integrations...",
    fullSelections: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    s: {
      mb: 5
    },
    label: t("ManageCompanyForm.preferredIntegrations"),
    name: "settings.preferredIntegrations",
    options: lists.integrations.types.names,
    control: control,
    placeholder: "Select Integrations...",
    fullSelections: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "settings.privateSlackChannelApprovals",
    label: t("ManageCompanyForm.privateSlackChannelApprovals"),
    control: control,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "settings.hideSlackPrivateMessages",
    label: t("ManageCompanyForm.hideSlackPrivateMessages"),
    control: control
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mt: 10,
      mb: 3
    }
  }, "Costs"), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    }
  }, t("ManageCompanyForm.costPerVolume")), Object.keys(DataUsageTypeEnum).map(key => /*#__PURE__*/React.createElement(InputTextControl, {
    name: `settings.costPerVolume.${key}`,
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      color: "textSecondary",
      s: {
        mr: 1
      }
    }, /*#__PURE__*/React.createElement(Badge, {
      color: lists.costs.types.colors[key],
      s: {
        ml: -1,
        mr: 1
      }
    }, lists.costs.types.names[key]), " ", "$"),
    s: {
      mb: 5
    },
    placeholder: "100",
    control: control,
    type: "number",
    required: true
  })), /*#__PURE__*/React.createElement(InputTextControl, {
    name: "settings.costAiAssistantCap",
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      color: "textSecondary",
      s: {
        mr: 1
      }
    }, "$"),
    label: t("ManageCompanyForm.costAiAssistantCap"),
    s: {
      mb: 5
    },
    control: control,
    type: "number",
    required: true
  }), /*#__PURE__*/React.createElement(InputTextControl, {
    name: "settings.costUsageCap",
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      color: "textSecondary",
      s: {
        mr: 1
      }
    }, "$"),
    label: t("ManageCompanyForm.costUsageCap"),
    control: control,
    type: "number",
    required: true
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mt: 10,
      mb: 3
    }
  }, "Detection"), /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "settings.memberBlacklist",
    label: t("ManageCompanyForm.memberBlacklist"),
    placeholder: "Select Members...",
    control: control,
    options: company?.members.map(_ref2 => {
      let {
        id,
        firstName,
        lastName,
        role,
        email,
        type,
        profileImage
      } = _ref2;
      return {
        id,
        showImage: true,
        image: profileImage,
        pre: role?.name,
        value: `${firstName} ${lastName}`,
        accessory: !!type && /*#__PURE__*/React.createElement(Picture, {
          border: "squared",
          src: lists.members.types.logos(theme)[type],
          width: "1.5rem",
          height: "1.5rem",
          backgroundSize: "contain",
          display: "inline-block",
          s: {
            mr: 1,
            mb: -1.5
          }
        }),
        post: email
      };
    }) || [],
    s: {
      mb: 5
    },
    fullWidth: true,
    withSearch: true,
    fullSelections: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "settings.eventBlacklist",
    label: t("ManageCompanyForm.eventBlacklist"),
    placeholder: "Select Event types...",
    control: control,
    options: helpers.integrationEventCombinations(integrationTypes),
    s: {
      mb: 5
    },
    withSearch: true,
    fullWidth: true,
    fullSelections: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "settings.topicEventDirectMapping",
    label: t("ManageCompanyForm.topicEventDirectMapping"),
    placeholder: "Select Event types...",
    control: control,
    options: helpers.integrationEventCombinations(integrationTypes),
    s: {
      mb: 5
    },
    withSearch: true,
    fullSelections: true,
    fullWidth: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    }
  }, "Detection Thresholds ", /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "(Meaningful words)")), /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    gap: 2
  }, helpers.integrationEventCombinations(integrationTypes).map(item => /*#__PURE__*/React.createElement(InputTextControl, {
    name: `settings.detectionThresholds.${item.id}`,
    control: control,
    type: "number",
    placeholder: "-",
    fullWidth: true,
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      s: {
        mr: 2
      }
    }, item.value, ":")
  }))), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mt: 10,
      mb: 3
    }
  }, "Static Data"), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.statusMapping")),
    label: t("ManageCompanyForm.statusMapping"),
    disabled: true,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.excludedSalesforceUsers")),
    label: t("ManageCompanyForm.excludedSalesforceUsers"),
    disabled: true,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.statusMapping")),
    label: t("ManageCompanyForm.statusMapping"),
    disabled: true,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.excludedSalesforceEmails")),
    label: t("ManageCompanyForm.excludedSalesforceEmails"),
    disabled: true,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.salesforceCustomFields")),
    label: t("ManageCompanyForm.salesforceCustomFields"),
    disabled: true,
    s: {
      mb: 5
    }
  }), /*#__PURE__*/React.createElement(InputTextArea, {
    value: JSON.stringify(watch("settings.salesforceStageMapping")),
    label: t("ManageCompanyForm.salesforceStageMapping"),
    disabled: true,
    s: {
      mb: 5
    }
  })));
};
export default ManageCompanyForm;